import { log } from "../../logger";
import { dispatchSSEEvent } from "./events";
import type { SSEEventType } from "@egalregal/shared";

/**
 * Module-level singleton SSE connection to /api/events.
 * Initialized once at app boot (main.tsx), persists across all renders.
 * Dispatches events to the global EventTarget via dispatchSSEEvent().
 *
 * Each browser tab generates a stable client UUID (persisted in sessionStorage)
 * and sends it as X-Client-Id on every SSE connection. The server logs the
 * first 7 chars on connect/disconnect so log lines are correlated per tab.
 */

const MAX_RECONNECT_DELAY = 120_000; // 2 min
const BASE_RECONNECT_DELAY = 1_000; // 1 s
const CLIENT_ID_KEY = "__regalegal_client_id";

let es: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let destroyed = false;
let currentClientId: string | null = null;

/** Return a stable per-tab UUID, generating one on first access. */
function getOrCreateClientId(): string {
  try {
    let id = sessionStorage.getItem(CLIENT_ID_KEY);
    if (id) return id;
    id = crypto.randomUUID();
    sessionStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    // sessionStorage unavailable (e.g. incognito edge case) — ephemeral fallback
    return crypto.randomUUID();
  }
}

const clientUUID = getOrCreateClientId();

function connect(): void {
  if (destroyed) return;
  if (es) {
    es.close();
    es = null;
  }

  const source = new EventSource(`/api/events?clientId=${encodeURIComponent(clientUUID)}`, {
    withCredentials: true,
  });
  es = source;

  source.onopen = () => {
    log.debug("[SSE] connection opened");
    reconnectAttempts = 0;
  };

  const handleEvent = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      if (event.type === "connected") {
        currentClientId = data.clientId ?? null;
        log.debug({ clientId: currentClientId }, "[SSE] handshake received");
      } else if (event.type === "ping") {
        // Server keepalive — intentionally no-op
      } else {
        dispatchSSEEvent(event.type as SSEEventType, data);
      }
    } catch (err) {
      log.error({ err }, "[SSE] parse error");
    }
  };

  const eventTypes: SSEEventType[] = ["container-updated", "media-uploaded"];
  source.addEventListener("connected", handleEvent);
  source.addEventListener("ping", handleEvent);
  for (const eventType of eventTypes) {
    source.addEventListener(eventType, handleEvent);
  }
  const removeEventListeners = () => {
    source.removeEventListener("connected", handleEvent);
    source.removeEventListener("ping", handleEvent);
    for (const eventType of eventTypes) {
      source.removeEventListener(eventType, handleEvent);
    }
  };

  source.onerror = err => {
    // Close explicitly to suppress the browser's built-in auto-reconnect.
    // Without this, both the browser reconnect AND our setTimeout(connect)
    // fire, creating a snowball of parallel connections.
    log.error({ err }, "[SSE] connection error, scheduling reconnect");
    removeEventListeners();
    source.close();
    es = null;
    if (destroyed) return;
    const delay = Math.min(
      BASE_RECONNECT_DELAY * 2 ** reconnectAttempts,
      MAX_RECONNECT_DELAY,
    );
    reconnectAttempts++;
    log.debug({ clientId: currentClientId, delay, attempt: reconnectAttempts }, "[SSE] scheduling reconnect");
    reconnectTimer = setTimeout(connect, delay);
  };
}

/** Start the SSE connection. No-op if already running. */
export function initSSE(): void {
  if (es || destroyed) return;
  destroyed = false;
  connect();
}

/** Tear down the SSE connection (e.g. on logout). */
export function destroySSE(): void {
  destroyed = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (es) {
    es.close();
    es = null;
  }
  reconnectAttempts = 0;
  currentClientId = null;
}
